import { useRequestEmailChange, useUpdateProfile } from '@myrentalmate/api-client';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import type { FormEvent } from 'react';

import {
  EMAIL_FIELD,
  FormFields,
  optional,
  PHONE_FIELD,
  required,
  useFormValues,
  type Field,
} from '../../components/FormFields';
import { okData } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatPhone } from '../../lib/contact';
import { PasswordSection } from './PasswordSection';
import { TwoFactorSection } from './TwoFactorSection';
import { activeOrgName, ROLE_LABELS, useMe, type Me } from '../../lib/me';

const PROFILE_FIELDS: Field[] = [
  { name: 'display_name', label: 'Your Name', required: true },
  PHONE_FIELD,
];

function ProfileForm({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const form = useFormValues(PROFILE_FIELDS, {
    display_name: me.display_name,
    phone: formatPhone(me.phone),
  });
  const { values, setValues } = form;
  const save = useUpdateProfile({
    mutation: {
      onSuccess: (response) => {
        const saved = okData(response);
        if (saved) setValues({ display_name: saved.display_name, phone: formatPhone(saved.phone) });
        return queryClient.invalidateQueries();
      },
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!form.validate()) return;
    save.mutate({
      data: { display_name: required(values, 'display_name'), phone: optional(values, 'phone') },
    });
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box component="form" onSubmit={submit}>
        <Typography variant="h6">Your Details</Typography>
        <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
          <Box sx={{ width: '100%' }}>
            <FormFields
              fields={PROFILE_FIELDS}
              values={values}
              errors={form.errors}
              onChange={form.onChange}
            />
          </Box>
          {save.isError && <Alert severity="error">{apiErrorMessage(save.error)}</Alert>}
          {save.isSuccess && <Alert severity="success">Saved.</Alert>}
          <Button type="submit" variant="contained" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}

function EmailForm({ me }: { me: Me }) {
  const fields: Field[] = [
    { ...EMAIL_FIELD, name: 'new_email', label: 'New Email', required: true },
    ...(me.has_password
      ? [
          {
            name: 'current_password',
            label: 'Current Password',
            required: true,
            type: 'password' as const,
          },
        ]
      : []),
  ];
  const form = useFormValues(fields, { new_email: '', current_password: '' });
  const { values, setValues } = form;
  const change = useRequestEmailChange({
    mutation: { onSuccess: () => setValues({ new_email: '', current_password: '' }) },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!form.validate()) return;
    change.mutate({
      data: {
        new_email: required(values, 'new_email'),
        current_password: optional(values, 'current_password'),
      },
    });
  }

  const message = okData(change.data)?.message;
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box component="form" onSubmit={submit}>
        <Typography variant="h6">Sign-In Email</Typography>
        <Typography color="text.secondary">
          You sign in with {me.email}. It changes only after you open the link we send to the new
          address, and we'll tell your current address when it does.
        </Typography>
        <Stack spacing={2} sx={{ alignItems: 'flex-start', mt: 1 }}>
          <Box sx={{ width: '100%' }}>
            <FormFields
              fields={fields}
              values={values}
              errors={form.errors}
              onChange={form.onChange}
            />
          </Box>
          {change.isError && <Alert severity="error">{apiErrorMessage(change.error)}</Alert>}
          {message && <Alert severity="info">{message}</Alert>}
          <Button type="submit" variant="outlined" disabled={change.isPending}>
            {change.isPending ? 'Sending…' : 'Send confirmation link'}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}

export function ProfilePage() {
  const { data: me } = useMe();
  if (!me) return null;
  const org = activeOrgName(me);
  return (
    <Stack spacing={3} sx={{ maxWidth: 560 }}>
      <div>
        <Typography variant="h5">My Profile</Typography>
        {org && <Typography color="text.secondary">{org}</Typography>}
      </div>
      <ProfileForm me={me} />
      <EmailForm me={me} />
      <PasswordSection me={me} />
      <TwoFactorSection me={me} />
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your Access
        </Typography>
        <Stack spacing={1}>
          {me.memberships.map((m) => (
            <Stack key={m.org_id} direction="row" sx={{ justifyContent: 'space-between', gap: 2 }}>
              <Typography>{m.org_name}</Typography>
              <Typography color="text.secondary">{ROLE_LABELS[m.role]}</Typography>
            </Stack>
          ))}
          {me.is_platform_admin && (
            <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 2 }}>
              <Typography>All customers</Typography>
              <Typography color="text.secondary">Platform admin</Typography>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
